import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, EMPTY, forkJoin, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ServiceRequestService } from '../../services/service-request.service';
import { ServiceRequestValidation } from '../../models/service-request-token.model';
import { ServiceModel } from '../../models/service.model';
import { ClientModel } from '../../models/client.model';
import { AddressModel } from '../../models/address.model';
import { ProductRequestModel } from '../../models/product-request.model';
import { MatDialog } from '@angular/material/dialog';
import {
  ServiceRequestSummaryDialogComponent,
  ServiceRequestSummaryDialogData,
} from './service-request-summary-dialog/service-request-summary-dialog.component';
import { ToastService } from '../../services/toast.service';
import { NgClass } from '@angular/common';
import { MatCard, MatCardTitle, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';

type TimeSlotOption = 1 | 2;

@Component({
    selector: 'app-service-request',
    templateUrl: './service-request.component.html',
    styleUrls: ['./service-request.component.scss'],
    imports: [NgClass, MatCard, MatCardTitle, MatIcon, MatCardContent]
})
export class ServiceRequestComponent implements OnInit, OnDestroy {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly serviceRequestSvc = inject<ServiceRequestService>(ServiceRequestService);
  private readonly dialog = inject(MatDialog);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);

  isLoading = false;
  errorMessage = '';
  successMessage = '';
  requestMetadata?: ServiceRequestValidation;
  serviceDetails?: ServiceModel;
  clientDetails?: ClientModel;
  clientAddresses: AddressModel[] = [];
  selectedAddressId?: string;
  selectedDate?: string;
  pendingComment = '';
  isSubmitting = false;
  internalToken?: string | null;

  readonly minDate: string = this.buildMinDate();
  readonly timeSlotOptions: { id: TimeSlotOption; label: string }[] = [
    { id: 1, label: 'Por la mañana' },
    { id: 2, label: 'Por la tarde' },
  ];

  private readonly subscriptions = new Subscription();
  private readonly selectedSlots = new Set<TimeSlotOption>();

  ngOnInit(): void {
    const paramsSub = this.activatedRoute.queryParamMap.subscribe((params) => {
      this.internalToken = params.get('token');
      if (!this.internalToken) {
        this.handleError(
          'Token no encontrado. Por favor, verifique el enlace recibido.'
        );
        return;
      }

      this.initializeRequest(this.internalToken);
    });

    this.subscriptions.add(paramsSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.serviceRequestSvc.clearSession();
  }

  private initializeRequest(token: string): void {
    this.isLoading = true;
    this.errorMessage = '';

    const initSub = this.serviceRequestSvc
      .validateRequestToken(token)
      .pipe(
        tap((validation) => (this.requestMetadata = validation)),
        switchMap((validation) =>
          forkJoin({
            service: this.serviceRequestSvc.getServiceDetails(
              validation.serviceId
            ),
            client: this.serviceRequestSvc.getClientByEmail(validation.email),
          })
        ),
        switchMap(({ service, client }) => {
          this.serviceDetails = service;
          this.clientDetails = client;

          if (!client?.id) {
            this.clientAddresses = [];
            return of([]);
          }

          return this.serviceRequestSvc
            .getClientAddresses(client.id.toString())
            .pipe(tap((addresses) => (this.clientAddresses = addresses)));
        }),
        catchError((error) => {
          console.error('Service request initialization failed', error);
          this.handleError(
            'No pudimos validar el enlace. Por favor, inténtelo nuevamente.'
          );
          return EMPTY;
        })
      )
      .subscribe({
        next: () => (this.isLoading = false),
        error: () => (this.isLoading = false),
        complete: () => (this.isLoading = false),
      });

    this.subscriptions.add(initSub);
  }

  private handleError(message: string): void {
    this.errorMessage = message;
    this.isLoading = false;
    this.requestMetadata = undefined;
    this.serviceDetails = undefined;
    this.clientDetails = undefined;
    this.clientAddresses = [];
    this.selectedAddressId = undefined;
    this.selectedDate = undefined;
    this.selectedSlots.clear();
  }

  selectAddress(address: AddressModel): void {
    const normalizedId = address?.id as unknown as string;
    if (!normalizedId) {
      return;
    }

    this.selectedAddressId = normalizedId;
    this.selectedDate = undefined;
    this.selectedSlots.clear();
  }

  onAddressKeydown(event: KeyboardEvent, address: AddressModel): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.selectAddress(address);
    }
  }

  isAddressSelected(address: AddressModel): boolean {
    return this.selectedAddressId === (address?.id as unknown as string);
  }

  trackAddressById = (index: number, address: AddressModel): string => {
    const id = address?.id as unknown as string | undefined;
    return id ?? `${index}`;
  };

  onDateChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedDate = input?.value || undefined;
    if (!this.selectedDate) {
      this.selectedSlots.clear();
    }
  }

  toggleSlot(option: TimeSlotOption): void {
    if (this.selectedSlots.has(option)) {
      this.selectedSlots.delete(option);
      return;
    }
    this.selectedSlots.add(option);
  }

  isSlotSelected(option: TimeSlotOption): boolean {
    return this.selectedSlots.has(option);
  }

  openDatePicker(input: HTMLInputElement): void {
    const element = input;
    const picker = (element as HTMLInputElement & { showPicker?: () => void })
      .showPicker;
    const previousReadonly = element.readOnly;
    element.readOnly = false;
    try {
      if (picker) {
        picker.call(element);
      } else {
        element.focus();
      }
    } catch {
      element.focus();
    } finally {
      element.readOnly = previousReadonly;
    }
  }

  onContinue(): void {
    if (!this.selectedAddressId || this.isSubmitting) {
      return;
    }

    const selectedAddress = this.getSelectedAddress();
    const summaryData: ServiceRequestSummaryDialogData = {
      serviceName: this.serviceDetails?.name ?? 'Servicio',
      serviceDescription: this.serviceDetails?.description ?? '',
      addressLine: selectedAddress
        ? `${selectedAddress.street} ${selectedAddress.number}`
        : 'Dirección seleccionada',
      scheduleDate: this.selectedDate,
      schedulePreferenceLabel: this.getSchedulePreferenceLabel(),
      comment: this.pendingComment,
    };

    const dialogRef = this.dialog.open(ServiceRequestSummaryDialogComponent, {
      width: '520px',
      data: summaryData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.confirmed) {
        this.pendingComment = result.comment ?? '';
        const payload = this.buildRequestModel(this.pendingComment);
        if (!payload) {
          this.toast.error(
            'Datos incompletos',
            'No pudimos armar la solicitud. Intente nuevamente.'
          );
          return;
        }
        this.submitServiceRequest(payload);
      }
    });
  }

  private buildMinDate(): string {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  }

  private buildRequestModel(description: string): ProductRequestModel | null {
    const clientId = this.clientDetails?.id as unknown as string | undefined;
    const productId = this.serviceDetails?.categoryId as unknown as string | undefined;
    const taskJobId = this.serviceDetails?.subCategoryId as unknown as string | undefined;

    if (!clientId || !this.selectedAddressId || !productId || !taskJobId) {
      return null;
    }

    const schedulePreference = this.getSchedulePreferenceValue();
    const scheduleDate = this.selectedDate
      ? new Date(`${this.selectedDate}T00:00:00`).toISOString()
      : undefined;

    return {
      clientId,
      addressId: this.selectedAddressId,
      description: description ?? '',
      productId,
      taskJobId,
      scheduleDate,
      schedulePreference,
    };
  }

  private submitServiceRequest(model: ProductRequestModel): void {
    this.isSubmitting = true;

    this.serviceRequestSvc.createProductRequest(model).subscribe({
      next: () => {
        if (this.internalToken) {
          this.serviceRequestSvc.markTokenAsUsed(this.internalToken).subscribe({
            next: () => { },
            error: () => {
              console.warn('Could not mark token as used');
            },
          });
        }

        this.isSubmitting = false;
        this.successMessage = 'Solicitud enviada con éxito. Gracias por confirmar tu servicio. En breve nos contactaremos.';
      },
      error: () => {
        this.isSubmitting = false;
        this.toast.error(
          'Error al solicitar',
          'No pudimos enviar la solicitud. Intentá nuevamente más tarde.'
        );
      },
    });
  }

  private getSelectedAddress(): AddressModel | undefined {
    return this.clientAddresses.find(
      (address) => (address?.id as unknown as string) === this.selectedAddressId
    );
  }

  private getSchedulePreferenceValue(): number {
    if (!this.selectedSlots.size) {
      return 3;
    }

    return Array.from(this.selectedSlots).reduce(
      (sum, slot) => sum + slot, 0
    );
  }

  getSchedulePreferenceLabel(): string {
    const preference = this.getSchedulePreferenceValue();

    switch (preference) {
      case 1:
        return 'Por la mañana';
      case 2:
        return 'Por la tarde';
      case 3:
      default:
        return 'Durante todo el día';
    }
  }
}
